import React from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  SimpleGrid,
  Card,
  ThemeIcon,
  Paper,
  Box,
  useComputedColorScheme,
} from '@mantine/core';
import { Shield, Zap, Package, Lock, Terminal, Stamp, ExternalLink } from 'lucide-react';
import { useLocation } from 'wouter';

const features = [
  { icon: Shield, title: 'Unbreakable IP Protection', description: 'Compile your Python source into native machine code before you ship. No bytecode, no .pyc files, no source reconstruction.' },
  { icon: Zap, title: 'Zero-Config Builds', description: 'No .spec files, no hidden imports, no config. Just pass the files you want to compile and you\'re done.' },
  { icon: Package, title: 'Self-Contained Binaries', description: 'Every executable bundles its own Python runtime. No system Python required on the target machine — ever.' },
  { icon: Lock, title: 'License Verification (Pro)', description: 'Protect your commercial software with JWT-based license keys signed with elliptic curve cryptography (P-256).' },
];

export default function Home() {
  const [, setLocation] = useLocation();
  const computedColorScheme = useComputedColorScheme('light');
  const codeBg = computedColorScheme === 'dark' ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-2)';
  const codeText = computedColorScheme === 'dark' ? 'gray.3' : 'dark.9';
  const cardBg = computedColorScheme === 'dark' ? undefined : 'var(--mantine-color-gray-1)';

  return (
    <Box component="main">
      {/* Hero */}
      <Container size="xl" py={60}>
        <Stack align="center" gap="lg" ta="center">
          <Group gap="xs" justify="center">
            <Terminal size={48} color="var(--mantine-color-blue-filled)" />
            <Title order={1} fz="clamp(40px, 6vw, 72px)" lh={1.1} style={{ letterSpacing: '-0.02em' }}>
              <Text span c="blue" inherit>Py</Text>
              <Text span inherit>2</Text>
              <Text span c="blue" inherit>Native</Text>
            </Title>
          </Group>
          <Title order={2} fz="clamp(30px, 4vw, 40px)" lh={1.2} style={{ letterSpacing: '-0.02em' }}>
            The Python Compiler
          </Title>
          <Text size="xl" c="dimmed" maw={700} lh={1.7}>
            Compile any Python script into a standalone native executable — without touching a single line of configuration.
          </Text>
          <Button
            size="lg"
            color="blue"
            rightSection={<Zap size={18} />}
            onClick={() => setLocation('/docs')}
            mt="md"
          >
            View Documentation
          </Button>
        </Stack>
      </Container>

      {/* Code Demo */}
      <Box bg="var(--mantine-color-body)" py={48}>
        <Container size="xl">
          <Stack align="center" gap="xl">
            <Paper
              p="xl"
              radius="lg"
              withBorder
              w="100%"
              maw={780}
              bg={codeBg}
              c={codeText}
              ff="monospace"
              fz="sm"
              lh={1.6}
              style={{ whiteSpace: 'pre', overflowX: 'auto' }}
            >
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'# Compile py2native itself \u2014 19 source files into one native .exe\n'}
              </Text>
              <Text span c="blue" fw="bold" ff="monospace" fz="sm" inherit>
                {'$'}
              </Text>
              <Text span ff="monospace" fz="sm" inherit>
                {' uv run py2native build --exe py2native --base src/py2native cli *.py\n\n'}
              </Text>
              <Text span ff="monospace" fz="sm" inherit>
                <Text span c="dimmed">{'INFO  '}</Text>
                <Text span c="cyan">{'pristine'}</Text>
                <Text span c="dimmed">{': Found existing pristine Python at\n'}</Text>
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      .py2native_build\\python\\cpython-3.14-windows-x86_64-none\n'}
              </Text>
              <Text span ff="monospace" fz="sm" inherit>
                <Text span c="dimmed">{'INFO  '}</Text>
                <Text span c="cyan">{'build'}</Text>
                <Text span c="dimmed">{': Compiling cli with sources build.py, cli.py,\n'}</Text>
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      compiler.py, embed.py, linuxplatform.py,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      macplatform.py, plugin.py, pristine.py,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      runtime.py, templates.py, utils.py, version.py,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      whl.py, winnovs.py, winplatform.py,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      wintemplate.py, xwinonlinux.py, __init__.py\n'}
              </Text>
              <Text span ff="monospace" fz="sm" inherit>
                <Text span c="dimmed">{'INFO  '}</Text>
                <Text span c="cyan">{'compiler'}</Text>
                <Text span c="dimmed">{': Compiling bootstrap.c (noInit=True)\n'}</Text>
              </Text>
              <Text span ff="monospace" fz="sm" inherit>
                <Text span c="dimmed">{'INFO  '}</Text>
                <Text span c="cyan">{'compiler'}</Text>
                <Text span c="dimmed">{': Compiling build.c, cli.c, compiler.c,\n'}</Text>
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      embed.c, linuxplatform.c, macplatform.c,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      plugin.c, pristine.c, runtime.c, templates.c,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      utils.c, version.c, whl.c, winnovs.c,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      winplatform.c, wintemplate.c, xwinonlinux.c,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      shared.c (noInit=False)\n'}
              </Text>
              <Text span ff="monospace" fz="sm" inherit>
                <Text span c="dimmed">{'INFO  '}</Text>
                <Text span c="cyan">{'compiler'}</Text>
                <Text span c="dimmed">{': Objects bootstrap.obj, build.obj, cli.obj,\n'}</Text>
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      compiler.obj, embed.obj, linuxplatform.obj,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      macplatform.obj, plugin.obj, pristine.obj,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      runtime.obj, templates.obj, utils.obj,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      version.obj, whl.obj, winnovs.obj,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      winplatform.obj, wintemplate.obj,\n'}
              </Text>
              <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                {'      xwinonlinux.obj, shared.obj\n'}
              </Text>
              <Text span c="green" fw="bold" ff="monospace" fz="sm" inherit>
                {'\nINFO  build: Compiled to .py2native_build\\py2native.exe'}
              </Text>
            </Paper>
          </Stack>
        </Container>
      </Box>

      {/* Features */}
      <Box py={48}>
        <Container size="xl">
          <Stack align="center" ta="center" mb={32}>
            <Title order={2} fz="clamp(30px, 4vw, 40px)" lh={1.2} style={{ letterSpacing: '-0.02em' }}>
              Stop wrestling with spec files.
            </Title>
            <Text size="xl" c="dimmed" maw={650} lh={1.6}>
              Built for proprietary software vendors, fintech algorithms, and internal tooling developers who just want to ship.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            {features.map((f, i) => (
              <Card key={i} padding="xl" radius="lg" withBorder bg={cardBg}>
                <ThemeIcon size={64} radius="md" variant="light" color="blue" mb="lg">
                  <f.icon size={28} />
                </ThemeIcon>
                <Title order={3} fz={24} mb="sm">{f.title}</Title>
                <Text c="dimmed" lh={1.6}>{f.description}</Text>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Comparison */}
      <Box py={48}>
        <Container size="xl">
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing={48}>
            <Stack gap="lg">
              <Text span c="red" fw="bold" fz={14} tt="uppercase" style={{ letterSpacing: 1 }}>
                The Old Way vs The New Way
              </Text>
              <Title order={2} fz="clamp(30px, 4vw, 40px)" lh={1.2} style={{ letterSpacing: '-0.02em' }}>
                Packaging shouldn't take longer than coding.
              </Title>
              <Text size="lg" c="dimmed" lh={1.6}>
                Standard tools force you to write massive configuration files, hunt down hidden dynamic imports, and pray the C-extensions don't break on the target machine.
              </Text>
              <Text size="lg" c="dimmed" lh={1.6}>
                Py2Native acts like a standard C compiler. You tell it what to protect, and it handles the rest. No setup.py, no .spec files, no headaches.
              </Text>
            </Stack>

            <Card padding="xl" radius="lg" withBorder>
              <Stack gap="xl">
                <Box>
                  <Group gap="xs" mb="sm">
                    <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-red-filled)' }} />
                    <Text span fw="bold" c="red">PyInstaller / Nuitka Workflow</Text>
                  </Group>
                  <Paper
                    p="md"
                    radius="md"
                    withBorder
                    bg={codeBg}
                    ff="monospace"
                    fz="sm"
                    c={codeText}
                    style={{ whiteSpace: 'pre', overflowX: 'auto' }}
                  >
                    <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                      {'# Hunt for hidden imports...\n'}
                    </Text>
                    <Text span ff="monospace" fz="sm" inherit>
                      {'python -m PyInstaller \\\n  --hidden-import=pandas._libs.tslibs \\\n  --hidden-import=numpy.core._multiarray_umath \\\n  --exclude-module=tkinter \\\n  --add-data="assets/*:assets" \\\n  --onefile app.py'}
                    </Text>
                  </Paper>
                </Box>
                <Box>
                  <Group gap="xs" mb="sm">
                    <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: 'var(--mantine-color-blue-filled)' }} />
                    <Text span fw="bold" c="blue">Py2Native Workflow</Text>
                  </Group>
                  <Paper
                    p="md"
                    radius="md"
                    withBorder
                    bg={codeBg}
                    ff="monospace"
                    fz="sm"
                    c={codeText}
                    style={{ whiteSpace: 'pre', overflowX: 'auto' }}
                  >
                    <Text span c="dimmed" ff="monospace" fz="sm" inherit>
                      {'# Just pass the files you want to compile.\n'}
                    </Text>
                    <Text span ff="monospace" fz="sm" inherit>
                      {'uv run py2native build app app.py'}
                    </Text>
                  </Paper>
                </Box>
              </Stack>
            </Card>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Cross-promotion: TimestampGIT */}
      <Box py={48}>
        <Container size="xl">
          <Paper p={48} radius="lg" withBorder bg={cardBg} ta="center">
            <Stack align="center" gap="lg">
              <ThemeIcon size={64} radius="md" variant="light" color="blue">
                <Stamp size={28} />
              </ThemeIcon>
              <Title order={2} fz="clamp(30px, 4vw, 40px)" lh={1.2} style={{ letterSpacing: '-0.02em' }}>
                Prove when your code existed.
              </Title>
              <Text size="lg" c="dimmed" maw={650} lh={1.6}>
                TimestampGIT automatically timestamps your GitHub repositories in the Bitcoin blockchain — cryptographic proof of when a specific part of your source code existed.
              </Text>
              <Button
                size="md"
                color="blue"
                component="a"
                href="https://timestampgit.dev"
                target="_blank"
                rel="noopener noreferrer"
                rightSection={<ExternalLink size={18} />}
              >
                Visit TimestampGIT
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

    </Box>
  );
}
